import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDoctorContext } from "../../Navigation/context/DoctorContext";
import { useToast } from "../../Common/hooks/useToast";
import { BottomNav } from "../../Common/custom_components/bottom_nav/BottomNav";
import { useDashboardModule } from "./hooks/dashboard_module.hook";
import style from "./dashboard.module.css";

export const DoctorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { incomingRequest, countdown, doctorProfile } = useDoctorContext();
  const { toast } = useToast();
  const {
    isLoading,
    activeConsultation,
    recentConsultations,
    ongoingTimer,
    handleAcceptRequest,
    handleRejectRequest,
  } = useDashboardModule();

  if (isLoading) {
    return (
      <div className={style.doctorBody}>
        {/* Mock Header Shimmer */}
        <div className={style.docHeader} style={{ position: "static" }}>
          <div className={style.docHeaderLeft}>
            <div className={`${style.shimmerItem} ${style.shimmerCircle}`} />
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <div className={style.shimmerItem} style={{ height: "16px", width: "120px" }} />
              <div className={style.shimmerItem} style={{ height: "10px", width: "80px" }} />
            </div>
          </div>
        </div>

        {/* Content Area Shimmer */}
        <div className={style.docContent}>
          {/* Incoming Requests Section Shimmer */}
          <div>
            <div className={`${style.shimmerItem} ${style.shimmerHeader}`} />
            <div className={`${style.shimmerItem} ${style.shimmerCard}`} style={{ marginBottom: "20px" }} />
          </div>

          {/* Ongoing Consultation Section Shimmer */}
          <div>
            <div className={`${style.shimmerItem} ${style.shimmerHeader}`} />
            <div className={`${style.shimmerItem} ${style.shimmerCard}`} style={{ marginBottom: "20px" }} />
          </div>

          {/* Recent Consultations Section Shimmer */}
          <div>
            <div className={`${style.shimmerItem} ${style.shimmerHeader}`} />
            <div className={`${style.shimmerItem} ${style.shimmerListItem}`} />
            <div className={`${style.shimmerItem} ${style.shimmerListItem}`} />
            <div className={`${style.shimmerItem} ${style.shimmerListItem}`} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={style.doctorBody}>
      {/* Toast */}
      {toast.show && (
        <div className={`${style.toastMsg} ${style.show} ${style[toast.type]}`}>
          <span className="material-icons-round">
            {toast.type === "success" ? "check_circle" : "cancel"}
          </span>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className={style.docHeader}>
        <div className={style.docHeaderLeft}>
          <img
            src="/logo-transparent3.png"
            alt="Pococare"
            style={{ height: "56px" }}
            onError={(e) => {
              (e.target as HTMLImageElement).src =
                "https://nice-stone-091e25e00.3.azurestaticapps.net/logo-transparent3.png";
            }}
          />
          <div className={style.docHeaderInfo}>
            <h1>Dr. {doctorProfile?.name || ""}</h1>
            <span>{doctorProfile?.specialization || ""}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={style.docContent}>

        {/* Incoming Requests */}
        <div>
          <div className={style.sectionHeader}>
            <h2>Incoming Requests</h2>
            {incomingRequest && <span className={style.badge}>New</span>}
          </div>
          {incomingRequest ? (
            <div className={style.requestCard}>
              <div className={style.requestHeader}>
                <div className={style.requestPatient}>
                  <div className={style.patientAvatar}>
                    {incomingRequest.patient_name
                      ? incomingRequest.patient_name.slice(0, 2).toUpperCase()
                      : "PT"}
                  </div>
                  <div>
                    <div className={style.patientName}>{incomingRequest.patient_name}</div>
                    <div className={style.patientTicket}>{String(incomingRequest.ticket_id)}</div>
                  </div>
                </div>
                <div className={style.requestMode}>
                  <span className="material-icons-round">videocam</span>
                  {incomingRequest.consultation_mode || "Video"}
                </div>
              </div>
              <div className={style.requestEmergency}>
                <span className="material-icons-round">warning</span>
                <span>Emergency Consult Request</span>
              </div>
              <div className={style.requestTimer}>
                <span className="material-icons-round">schedule</span>
                Auto-declining in {countdown}s
              </div>
              <div className={style.requestActions}>
                <button
                  className={style.btnAccept}
                  onClick={() => handleAcceptRequest(incomingRequest.consultation_id)}
                >
                  <span className="material-icons-round" style={{ fontSize: "18px" }}>check</span>
                  Accept
                </button>
                <button
                  className={style.btnReject}
                  onClick={() => handleRejectRequest(incomingRequest.consultation_id)}
                >
                  <span className="material-icons-round" style={{ fontSize: "18px" }}>close</span>
                  Reject
                </button>
              </div>
            </div>
          ) : (
            <div style={{
              background: "var(--white)", borderRadius: "16px", padding: "16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)", border: "1.5px dashed var(--outline)",
              minHeight: "180px", display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", gap: "8px",
            }}>
              <span className="material-icons-round" style={{ fontSize: "32px", color: "var(--placeholder)", opacity: 0.4 }}>inbox</span>
              <span style={{ fontSize: "13px", fontWeight: 500, color: "var(--placeholder)" }}>No incoming requests right now</span>
              <span style={{ fontSize: "11px", color: "var(--placeholder)", opacity: 0.7 }}>New requests will appear here</span>
            </div>
          )}
        </div>

        {/* Ongoing Consultation */}
        <div>
          <div className={style.sectionHeader}>
            <h2>Ongoing Consultation</h2>
          </div>
          {activeConsultation ? (
            <div className={style.ongoingCard}>
              <div className={style.ongoingHeader}>
                <div className={style.ongoingLabel}>
                  <span className={style.dot} />
                  Live Consultation
                </div>
                <div className={style.ongoingTimer}>
                  <span className="material-icons-round" style={{ fontSize: "16px" }}>schedule</span>
                  {ongoingTimer}
                </div>
              </div>
              <div className={style.ongoingPatient}>{activeConsultation.patient_name || "Patient"}</div>
              <div className={style.ongoingTicket}>
                {String(activeConsultation.ticket_id || "N/A")} • {activeConsultation.consultation_mode || "Video"} Call
              </div>
              <button
                className={style.btnJoin}
                onClick={() => navigate(`/consultation/${activeConsultation._id}`)}
              >
                <span className="material-icons-round" style={{ fontSize: "18px" }}>videocam</span>
                Join Consultation
              </button>
            </div>
          ) : (
            <div style={{
              background: "var(--white)", borderRadius: "16px", padding: "16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)", border: "1.5px dashed var(--outline)",
              minHeight: "180px", display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center", gap: "8px",
            }}>
              <span className="material-icons-round" style={{ fontSize: "32px", color: "var(--placeholder)", opacity: 0.4 }}>videocam_off</span>
              <span style={{ fontSize: "13px", fontWeight: 500, color: "var(--placeholder)" }}>No ongoing consultation</span>
            </div>
          )}
        </div>

        {/* Recent */}
        <div>
          <div className={style.sectionHeader}>
            <h2>Recent</h2>
            <Link to="/history" style={{ color: "var(--primary)", fontWeight: 600, fontSize: "13px", textDecoration: "none" }}>
              View All
            </Link>
          </div>
          <div className={style.recentList}>
            {recentConsultations.length === 0 ? (
              <div style={{ textAlign: "center", padding: "24px", color: "var(--placeholder)" }}>
                No recent consultations.
              </div>
            ) : (
              recentConsultations.map((item) => (
                <div
                  key={item._id}
                  className={style.recentItem}
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/consultation/${item._id}/detail`)}
                >
                  <div
                    className={style.recentIcon}
                    style={{
                      background: item.status === "completed" ? "var(--primary-container)" : "#FEE2E2",
                      color: item.status === "completed" ? "var(--primary)" : "var(--error)",
                    }}
                  >
                    <span className="material-icons-round">
                      {item.status === "completed" ? "check_circle" : "cancel"}
                    </span>
                  </div>
                  <div className={style.recentInfo}>
                    <div className={style.recentName}>{item.patient_name}</div>
                    <div className={style.recentMeta}>
                      {new Date(item.requested_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })},{" "}
                      {new Date(item.requested_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} •{" "}
                      {item.consultation_mode || "Video"}
                    </div>
                  </div>
                  <span className={style.recentDuration}>
                    {item.call_duration_seconds
                      ? `${Math.round(item.call_duration_seconds / 60)} min`
                      : "N/A"}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <BottomNav notifBadge={incomingRequest ? 1 : 0} />
    </div>
  );
};
